/**
 * Real integration API (broker-based OAuth on the Nest backend). Replaces the
 * old localStorage mock. Connecting is a full-page redirect to the broker's
 * consent flow (cookie auth travels with the navigation); listing + disconnect
 * are normal JSON calls.
 */

import { apiClient, ApiUrls } from '@ideoz/core';

/** A connected integration row, as returned by `GET /integration/user-integrations`. */
export interface UserIntegration {
  id: string;
  provider: string;
}

export async function listUserIntegrations(): Promise<UserIntegration[]> {
  const res = await apiClient.get(ApiUrls.userIntegrations());
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * Start the OAuth connect flow.
 *
 * The `initiate` endpoint is Bearer-guarded, and a top-level browser navigation
 * can't send the Authorization header — so we can't just point the browser at
 * it (that 401s). Instead we GET it via apiClient (header attached) to obtain
 * the broker consent URL, then navigate the browser there. The broker leg is
 * unauthenticated (the user is encoded in `client_state`), so the navigation
 * needs no header. Requires the backend `initiate` route to return `{ url }`
 * instead of a 302 redirect.
 */
export async function connectIntegration(provider: string): Promise<void> {
  const res = await apiClient.get(ApiUrls.integrationInitiate(provider));
  const url = res.data?.url;
  if (!url) {
    throw new Error('Connect flow did not return a broker URL');
  }
  window.location.assign(url);
}

export async function disconnectIntegration(userIntegrationId: string): Promise<void> {
  await apiClient.post(ApiUrls.integrationDisconnect(), { userIntegrationId });
}
